import type { InvalidUser } from '@/queries/userUpload/userUploadTypes'

export type ClassifiedError = {
  key: string
  field: string
  message: string
}

/**
 * Sub-categorise `base` errors by matching the English message. The backend
 * groups all unstructured failures (duplicates, missing identifiers,
 * unlinkable managers, …) under `errors.base`, so the FE has to derive a
 * stable key from the message text to drive the error filter.
 *
 * This couples the FE filter buckets to the exact English wording the API
 * returns. If the backend ever localises these strings or changes the
 * phrasing, the regexes will stop matching and every base error will
 * silently collapse into the generic `base` bucket. Replace with a
 * backend-provided `code`/`key` per error as soon as one is available.
 */
const BASE_CLASSIFIERS: Record<string, RegExp> = {
  base_duplicate: /duplicate/i,
  base_unlinkable_manager: /manager.*link|link.*manager/i,
  base_blank_identifier: /blank/i,
}

/** Bucket a base error into a finer key via regex; non-base fields pass through. */
const classifyError = (field: string, message: string): string => {
  if (field !== 'base') return field
  for (const [key, pattern] of Object.entries(BASE_CLASSIFIERS)) {
    if (pattern.test(message)) return key
  }
  return 'base'
}

/** Flatten user.errors into a list of classified entries (one per message). */
export const getClassifiedErrors = (user: InvalidUser): ClassifiedError[] =>
  Object.entries(user.errors).flatMap(([field, messages]) =>
    (messages ?? []).map((message) => ({
      key: classifyError(field, message),
      field,
      message,
    }))
  )
